import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { formatDateTime } from "../../lib/date";
import { toast } from "sonner";
import {
  Sparkles, Play, Loader2, Plus, Trash2, Info, TrendingUp, TrendingDown, Minus, Bot,
  MessageSquare, AlertTriangle, Copy, Wand2, History,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SENT_TONE = { positive: "#0E6B3F", neutral: "#6B5600", negative: "#C23A00" };

const shortLabel = (iso) => {
  if (!iso) return "";
  return `${iso.slice(8, 10)}-${iso.slice(5, 7)}`;
};

// Delta indicator: shows +/- change vs a baseline
function DeltaBadge({ current, baseline, label, testid }) {
  if (baseline == null) return null;
  const d = Math.round((current - baseline) * 10) / 10;
  const up = d > 0, down = d < 0;
  const color = up ? "#0E6B3F" : down ? "#C23A00" : "#8A857A";
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color }} data-testid={testid}>
      <Icon className="w-3 h-3" />
      {d > 0 ? "+" : ""}{d}% <span className="text-[#8A857A] font-normal">{label}</span>
    </span>
  );
}

export default function AIVisibility() {
  const [latest, setLatest] = useState(null);
  const [trend, setTrend] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [config, setConfig] = useState({ brand: "EQUIPR", engines: [], categories: [] });
  const [running, setRunning] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingGap, setSendingGap] = useState(false);
  const [briefBusy, setBriefBusy] = useState("");
  const [brief, setBrief] = useState(null);
  const [briefOpen, setBriefOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newPrompt, setNewPrompt] = useState("");
  const [newCat, setNewCat] = useState("General");
  const [newComp, setNewComp] = useState("");
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [l, p, c, cfg] = await Promise.all([
        axios.get(`${API}/ai-visibility/latest`),
        axios.get(`${API}/ai-visibility/prompts`),
        axios.get(`${API}/ai-visibility/competitors`),
        axios.get(`${API}/ai-visibility/config`),
      ]);
      setLatest(l.data.latest);
      setTrend(l.data.trend || []);
      setPrompts(p.data.prompts || []);
      setCompetitors(c.data.competitors || []);
      setConfig(cfg.data);
      if (l.data.running_run_id) { setRunning(true); pollRun(l.data.running_run_id); }
    } catch (e) {
      toast.error("Failed to load AI Visibility");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, [load]);

  const pollRun = (runId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/ai-visibility/runs/${runId}`);
        if (r.data.status !== "running") {
          clearInterval(pollRef.current);
          setRunning(false);
          toast.success("AI Visibility scan complete");
          load();
        }
      } catch { clearInterval(pollRef.current); setRunning(false); }
    }, 4000);
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/ai-visibility/run`);
      toast.info("Scanning AI engines…");
      pollRun(r.data.run_id);
    } catch (e) {
      setRunning(false);
      toast.error(e?.response?.data?.detail || "Failed to start scan");
    }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const r = await axios.post(`${API}/ai-visibility/send-digest`);
      toast.success(`Digest sent to ${r.data.sent || 0} leader(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Digest failed");
    } finally { setSendingDigest(false); }
  };

  const sendGapAlert = async () => {
    setSendingGap(true);
    try {
      const r = await axios.post(`${API}/ai-visibility/gap-alert`);
      toast.success(r.data.gaps ? `Gap alert sent to ${r.data.sent || 0} leader(s) — ${r.data.gaps} gap(s)` : "No vertical gaps right now 🎉");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gap alert failed");
    } finally { setSendingGap(false); }
  };

  const genBrief = async (g) => {
    setBriefBusy(g.prompt);
    try {
      const r = await axios.post(`${API}/ai-visibility/gap-brief`, { prompt: g.prompt, category: g.category, top_competitor: g.top_competitor });
      setBrief({ ...r.data.brief, _q: g.prompt, _cat: g.category });
      setBriefOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate brief");
    } finally { setBriefBusy(""); }
  };

  const copyBrief = () => {
    if (!brief) return;
    const txt = `Title: ${brief.title}\nTarget question: ${brief.target_question}\nAngle: ${brief.angle || ""}\nFormat: ${brief.suggested_format || ""}\n\nKey points:\n${(brief.key_points || []).map(k => `- ${k}`).join("\n")}\n\nKeywords: ${(brief.keywords || []).join(", ")}\nMeta: ${brief.meta_description || ""}\nCTA: ${brief.cta || ""}`;
    navigator.clipboard.writeText(txt);
    toast.success("Brief copied");
  };

  const addPrompt = async () => {
    if (!newPrompt.trim()) return;
    try { await axios.post(`${API}/ai-visibility/prompts`, { text: newPrompt, category: newCat }); setNewPrompt(""); load(); }
    catch { toast.error("Failed to add prompt"); }
  };
  const delPrompt = async (id) => { try { await axios.delete(`${API}/ai-visibility/prompts/${id}`); load(); } catch { toast.error("Failed"); } };
  const addComp = async () => {
    if (!newComp.trim()) return;
    try { await axios.post(`${API}/ai-visibility/competitors`, { name: newComp }); setNewComp(""); load(); }
    catch { toast.error("Failed to add competitor"); }
  };
  const delComp = async (id) => { try { await axios.delete(`${API}/ai-visibility/competitors/${id}`); load(); } catch { toast.error("Failed"); } };

  const s = latest?.summary || {};
  const sov = s.share_of_voice || {};
  const notConnected = (config.engines || []).filter(e => !e.connected);
  const connected = (config.engines || []).filter(e => e.connected);
  const results = latest?.results || [];

  // Deltas: current vs previous scan and vs ~7 days ago
  const cur = trend.length ? trend[trend.length - 1] : null;
  const prevScan = trend.length > 1 ? trend[trend.length - 2] : null;
  let weekAgo = null;
  if (cur?.date) {
    const weekAgoTs = new Date(cur.date).getTime() - 7 * 86400000;
    for (let i = trend.length - 2; i >= 0; i--) {
      if (new Date(trend[i].date).getTime() <= weekAgoTs) { weekAgo = trend[i]; break; }
    }
  }

  return (
    <div className="space-y-4" data-testid="ai-visibility-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm text-[#59554D]">
          <Sparkles className="w-4 h-4 text-[#1D4ED8]" />
          Tracks how often <b className="text-[#1A1A1A] mx-1">{config.brand}</b> is recommended by AI assistants vs competitors
        </div>
        <div className="flex items-center gap-3">
          {latest?.started_at && <span className="text-xs text-[#8A857A]">Last scan: {formatDateTime(latest.started_at)}</span>}
          <Button variant="outline" onClick={sendDigest} disabled={sendingDigest}
            className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED]" data-testid="aiv-send-digest">
            {sendingDigest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />} Email leaders
          </Button>
          <Button variant="outline" onClick={sendGapAlert} disabled={sendingGap}
            className="border-[#FFE9E0] bg-transparent text-[#C23A00] hover:bg-[#FFE9E0]" data-testid="aiv-send-gap">
            {sendingGap ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />} Gap alert
          </Button>
          <Button onClick={runNow} disabled={running} className="bg-[#1D4ED8] hover:bg-[#EFF6FF] text-[#1A1A1A] font-medium" data-testid="aiv-run">
            {running ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />} {running ? "Scanning…" : "Run scan now"}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="aiv-engine-chips">
        {(config.engines || []).map(e => (
          <span key={e.key} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] ${e.connected ? "border-[#E3F3E9] bg-[#E3F3E9] text-[#0E6B3F]" : "border-[#DAD5CA] bg-[#FFFFFF] text-[#8A857A]"}`} data-testid={`aiv-engine-${e.key}`}>
            <Bot className="w-3 h-3" /> {e.label}
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: e.connected ? "#0E6B3F" : "#8A857A" }} />
          </span>
        ))}
      </div>

      {notConnected.length > 0 && (
        <div className="rounded-lg border border-[#FFF8D6] bg-[#FFF8D6] p-3 flex items-start gap-2 text-xs text-[#59554D]" data-testid="aiv-engine-notice">
          <Info className="w-4 h-4 text-[#8A6D00] shrink-0 mt-0.5" />
          <span>Currently scanning <b className="text-[#1A1A1A]">{connected.map(e => e.label.split(" (")[0]).join(", ") || "no engines"}</b>. Add API keys for <b className="text-[#1A1A1A]">{notConnected.map(e => e.label).join(", ")}</b> to track how those engines mention {config.brand} too.</span>
        </div>
      )}

      {loading ? (
        <div className="p-10 text-center text-[#8A857A]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : !latest ? (
        <Card className="bg-[#FFFFFF] border-[#DAD5CA]"><CardContent className="p-10 text-center space-y-2">
          <Bot className="w-10 h-10 mx-auto text-[#8A857A]" />
          <div className="text-[#1A1A1A] font-medium">No scans yet</div>
          <p className="text-sm text-[#59554D]">Click "Run scan now" to ask the AI engines your prompt set and see where {config.brand} shows up.</p>
        </CardContent></Card>
      ) : (
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="bg-[#FFFFFF] border border-[#DAD5CA]" data-testid="aiv-tabs">
            <TabsTrigger value="overview" data-testid="aiv-tab-overview">Overview &amp; Trends</TabsTrigger>
            <TabsTrigger value="prompts" data-testid="aiv-tab-prompts">Prompts</TabsTrigger>
            <TabsTrigger value="competitors" data-testid="aiv-tab-competitors">Competitors</TabsTrigger>
            <TabsTrigger value="gaps" data-testid="aiv-tab-gaps">Gaps &amp; Recommendations</TabsTrigger>
            <TabsTrigger value="history" data-testid="aiv-tab-history">Run history</TabsTrigger>
          </TabsList>

          {/* ---------------- OVERVIEW & TRENDS ---------------- */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]" data-testid="aiv-mention-rate"><CardContent className="p-4">
                <div className="text-2xl font-semibold text-[#1A1A1A]">{s.mention_rate ?? 0}%</div>
                <div className="text-xs text-[#8A857A] mb-1.5">Mention rate ({s.mentions}/{s.total} answers)</div>
                <div className="flex flex-col gap-0.5">
                  <DeltaBadge current={s.mention_rate ?? 0} baseline={prevScan?.mention_rate} label="vs last scan" testid="aiv-mr-delta-scan" />
                  <DeltaBadge current={s.mention_rate ?? 0} baseline={weekAgo?.mention_rate} label="vs last week" testid="aiv-mr-delta-week" />
                </div>
              </CardContent></Card>
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]"><CardContent className="p-4">
                <div className="text-2xl font-semibold text-[#1D4ED8]">{sov[config.brand] ?? 0}%</div>
                <div className="text-xs text-[#8A857A] mb-1.5">Share of voice</div>
                <div className="flex flex-col gap-0.5">
                  <DeltaBadge current={sov[config.brand] ?? 0} baseline={prevScan?.sov} label="vs last scan" testid="aiv-sov-delta-scan" />
                  <DeltaBadge current={sov[config.brand] ?? 0} baseline={weekAgo?.sov} label="vs last week" testid="aiv-sov-delta-week" />
                </div>
              </CardContent></Card>
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]"><CardContent className="p-4">
                <div className="text-2xl font-semibold text-[#8A6D00]">{(s.engines_available || []).length}</div>
                <div className="text-xs text-[#8A857A]">Engines scanned</div></CardContent></Card>
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]"><CardContent className="p-4">
                <div className="flex gap-2 items-center">
                  {["positive", "neutral", "negative"].map(k => (
                    <span key={k} className="text-sm font-semibold" style={{ color: SENT_TONE[k] }}>{s.sentiment?.[k] ?? 0}</span>
                  ))}
                </div>
                <div className="text-xs text-[#8A857A]">Sentiment (pos/neu/neg)</div></CardContent></Card>
            </div>

            {/* Trend chart */}
            <Card className="bg-[#FFFFFF] border-[#DAD5CA]" data-testid="aiv-trend-chart"><CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold mb-3 flex items-center gap-2"><TrendingUp className="w-3.5 h-3.5" /> Week-on-week trend</div>
              {trend.length > 1 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={trend} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F4F2ED" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={shortLabel} stroke="#8A857A" fontSize={11} tickLine={false} axisLine={{ stroke: "#DAD5CA" }} />
                    <YAxis unit="%" domain={[0, 100]} stroke="#8A857A" fontSize={11} tickLine={false} axisLine={false} width={52} />
                    <Tooltip
                      contentStyle={{ background: "#FFFFFF", border: "1px solid #DAD5CA", borderRadius: 8, fontSize: 12, color: "#fff" }}
                      labelFormatter={(v) => shortLabel(v)}
                      formatter={(val, name) => [`${val}%`, name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="mention_rate" name="Mention rate" stroke="#1D4ED8" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                    <Line type="monotone" dataKey="sov" name={`${config.brand} share of voice`} stroke="#8A6D00" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-[#8A857A] py-6 text-center">Run at least two scans to see the week-on-week trend.</p>
              )}
            </CardContent></Card>

            {/* Share of voice */}
            <Card className="bg-[#FFFFFF] border-[#DAD5CA]"><CardContent className="p-4 space-y-2">
              <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold">Share of voice</div>
              {Object.entries(sov).sort((a, b) => b[1] - a[1]).map(([name, pct]) => (
                <div key={name} className="flex items-center gap-3 text-sm">
                  <span className={`w-40 truncate ${name === config.brand ? "text-[#1A1A1A] font-medium" : "text-[#59554D]"}`}>{name}</span>
                  <div className="flex-1 h-2 rounded-full bg-[#F4F2ED] overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: name === config.brand ? "#1D4ED8" : "#C9C3B6" }} />
                  </div>
                  <span className="w-12 text-right text-[#8A857A]">{pct}%</span>
                </div>
              ))}
            </CardContent></Card>

            {/* By industry vertical */}
            {s.by_category && Object.keys(s.by_category).length > 0 && (
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]" data-testid="aiv-by-category"><CardContent className="p-4 space-y-2">
                <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold">Mention rate by industry</div>
                {Object.entries(s.by_category).sort((a, b) => (b[1].rate || 0) - (a[1].rate || 0)).map(([cat, d]) => (
                  <div key={cat} className="flex items-center gap-3 text-sm" data-testid={`aiv-cat-${cat}`}>
                    <span className="w-52 truncate text-[#59554D]">{cat}</span>
                    <div className="flex-1 h-2 rounded-full bg-[#F4F2ED] overflow-hidden">
                      <div className="h-full rounded-full bg-[#1D4ED8]" style={{ width: `${d.rate || 0}%` }} />
                    </div>
                    <span className="w-24 text-right text-[#8A857A]">{d.rate}% ({d.mentions}/{d.total})</span>
                  </div>
                ))}
              </CardContent></Card>
            )}

            {/* Engine × Industry grid */}
            {s.by_engine_category && (config.engines || []).some(e => e.connected) && (s.categories || []).length > 0 && (
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]" data-testid="aiv-engine-grid"><CardContent className="p-4 space-y-3">
                <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold">Mention rate — engine × industry</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[#8A857A] text-left">
                        <th className="py-1 pr-3 font-medium">Industry</th>
                        {(config.engines || []).filter(e => e.connected).map(e => (
                          <th key={e.key} className="py-1 px-3 font-medium text-center">{e.label.split(" (")[0]}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(s.categories || []).map(cat => (
                        <tr key={cat} className="border-t border-[#F4F2ED]" data-testid={`aiv-grid-row-${cat}`}>
                          <td className="py-2 pr-3 text-[#59554D]">{cat}</td>
                          {(config.engines || []).filter(e => e.connected).map(e => {
                            const cell = (s.by_engine_category[e.key] || {})[cat];
                            if (!cell || !cell.total) return <td key={e.key} className="py-2 px-3 text-center text-[#C9C3B6]">—</td>;
                            const tone = cell.rate >= 50 ? "#0E6B3F" : cell.rate > 0 ? "#6B5600" : "#C23A00";
                            return (
                              <td key={e.key} className="py-2 px-3 text-center">
                                <span className="inline-block rounded px-2 py-0.5 text-xs font-medium" style={{ background: `${tone}1A`, color: tone }}>
                                  {cell.rate}%
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent></Card>
            )}
          </TabsContent>

          {/* ---------------- PROMPTS ---------------- */}
          <TabsContent value="prompts" className="mt-4">
            <Card className="bg-[#FFFFFF] border-[#DAD5CA]"><CardContent className="p-4 space-y-2">
              <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold">Buyer-intent prompts</div>
              <div className="flex gap-2">
                <select value={newCat} onChange={(e) => setNewCat(e.target.value)}
                  className="bg-[#F4F2ED] border border-[#DAD5CA] text-[#1A1A1A] text-sm rounded-md px-2 shrink-0" data-testid="aiv-new-cat">
                  {(config.categories || ["General"]).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <Input value={newPrompt} onChange={(e) => setNewPrompt(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addPrompt()}
                  placeholder="e.g. best crane fleet management software" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="aiv-new-prompt" />
                <Button onClick={addPrompt} className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A] shrink-0" data-testid="aiv-add-prompt"><Plus className="w-4 h-4" /></Button>
              </div>
              {(() => {
                const order = [...(config.categories || []), "General"];
                const grouped = {};
                prompts.forEach(p => { const c = p.category || "General"; (grouped[c] = grouped[c] || []).push(p); });
                const cats = [...new Set([...order, ...Object.keys(grouped)])].filter(c => grouped[c]);
                return cats.map(cat => (
                  <div key={cat} className="pt-2">
                    <div className="text-[10px] uppercase tracking-wider text-[#1D4ED8] font-semibold mb-1">{cat} · {grouped[cat].length}</div>
                    {grouped[cat].map(p => (
                      <div key={p.id} className="flex items-center gap-2 text-sm text-[#59554D] py-1" data-testid={`aiv-prompt-${p.id}`}>
                        <span className="flex-1">{p.text}</span>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-[#C23A00] hover:bg-[#FFE9E0]" onClick={() => delPrompt(p.id)} data-testid={`aiv-del-prompt-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    ))}
                  </div>
                ));
              })()}
            </CardContent></Card>
          </TabsContent>

          {/* ---------------- COMPETITORS ---------------- */}
          <TabsContent value="competitors" className="mt-4">
            <Card className="bg-[#FFFFFF] border-[#DAD5CA]"><CardContent className="p-4 space-y-2">
              <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold">Competitors tracked</div>
              <div className="flex gap-2">
                <Input value={newComp} onChange={(e) => setNewComp(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComp()}
                  placeholder="Competitor brand name" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="aiv-new-comp" />
                <Button onClick={addComp} className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A] shrink-0" data-testid="aiv-add-comp"><Plus className="w-4 h-4" /></Button>
              </div>
              {competitors.map(c => (
                <div key={c.id} className="flex items-center gap-2 text-sm text-[#59554D] py-1" data-testid={`aiv-comp-${c.id}`}>
                  <span className="flex-1">{c.name}</span>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-[#C23A00] hover:bg-[#FFE9E0]" onClick={() => delComp(c.id)} data-testid={`aiv-del-comp-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
            </CardContent></Card>
          </TabsContent>

          {/* ---------------- GAPS & RECOMMENDATIONS ---------------- */}
          <TabsContent value="gaps" className="space-y-4 mt-4">
            {Array.isArray(s.gaps) && (
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]" data-testid="aiv-gaps"><CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-[#C23A00]" /> Visibility gaps ({config.brand} not mentioned)
                  </div>
                  <span className="text-[11px] text-[#8A857A]">{s.gaps.length} gap(s)</span>
                </div>
                {s.gaps.length === 0 ? (
                  <p className="text-sm text-[#0E6B3F]">Mentioned in every answer 🎉</p>
                ) : (
                  s.gaps.slice(0, 20).map((g, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm py-1.5 border-t border-[#F4F2ED] first:border-0" data-testid={`aiv-gap-${i}`}>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 mt-0.5 ${g.category !== "General" ? "bg-[#FFE9E0] text-[#C23A00]" : "bg-[#DAD5CA] text-[#59554D]"}`}>{g.category}</span>
                      <span className="flex-1 text-[#59554D]">{g.prompt}</span>
                      <span className="text-[11px] text-[#8A857A] shrink-0 hidden md:inline">
                        {(config.engines || []).find(e => e.key === g.engine)?.label?.split(" (")[0] || g.engine}
                        {g.top_competitor ? ` · ${g.top_competitor}` : ""}
                      </span>
                      <Button size="sm" variant="ghost" onClick={() => genBrief(g)} disabled={briefBusy === g.prompt}
                        className="h-6 px-2 text-[11px] text-[#1D4ED8] hover:bg-[#EFF6FF] shrink-0" data-testid={`aiv-gap-brief-${i}`}>
                        {briefBusy === g.prompt ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Wand2 className="w-3 h-3 mr-1" /> Brief</>}
                      </Button>
                    </div>
                  ))
                )}
              </CardContent></Card>
            )}

            {/* Latest answers */}
            <Card className="bg-[#FFFFFF] border-[#DAD5CA]"><CardContent className="p-0">
              <div className="px-4 py-2 border-b border-[#DAD5CA] text-[10px] uppercase tracking-wider text-[#8A857A] font-semibold flex items-center gap-2"><MessageSquare className="w-3.5 h-3.5" /> Latest answers</div>
              {results.filter(r => r.status === "ok").map((r, i) => (
                <div key={i} className="px-4 py-3 border-b border-[#F4F2ED] text-sm" data-testid={`aiv-result-${i}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-[#1D4ED8] text-[#1D4ED8] border-0 text-[10px]">{r.engine}</Badge>
                    <span className="text-[#59554D] flex-1 truncate">{r.prompt_text}</span>
                    {r.brand_mentioned
                      ? <Badge className="bg-[#E3F3E9] text-[#0E6B3F] border-0 text-[10px]">Mentioned · #{r.brand_rank}</Badge>
                      : <Badge className="bg-[#8A857A] text-[#8A857A] border-0 text-[10px]">Not mentioned</Badge>}
                    {r.sentiment && <Badge className="border-0 text-[10px]" style={{ background: `${SENT_TONE[r.sentiment]}1A`, color: SENT_TONE[r.sentiment] }}>{r.sentiment}</Badge>}
                  </div>
                  {r.competitors_mentioned?.length > 0 && <div className="text-xs text-[#8A857A] mt-1">Also named: {r.competitors_mentioned.join(", ")}</div>}
                </div>
              ))}
            </CardContent></Card>
          </TabsContent>

          {/* ---------------- RUN HISTORY ---------------- */}
          <TabsContent value="history" className="mt-4">
            <Card className="bg-[#FFFFFF] border-[#DAD5CA]" data-testid="aiv-run-history"><CardContent className="p-0">
              <div className="px-4 py-2 border-b border-[#DAD5CA] text-[10px] uppercase tracking-wider text-[#8A857A] font-semibold flex items-center gap-2"><History className="w-3.5 h-3.5" /> Past scans</div>
              {trend.length === 0 ? (
                <p className="p-4 text-sm text-[#8A857A]">No scans recorded yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[#8A857A] text-left">
                      <th className="py-2 px-4 font-medium">Date</th>
                      <th className="py-2 px-4 font-medium text-right">Mention rate</th>
                      <th className="py-2 px-4 font-medium text-right">{config.brand} share of voice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...trend].reverse().map((t, i) => (
                      <tr key={i} className="border-t border-[#F4F2ED]" data-testid={`aiv-history-row-${i}`}>
                        <td className="py-2 px-4 text-[#59554D]">{formatDateTime(t.date)}</td>
                        <td className="py-2 px-4 text-right text-[#1D4ED8] font-medium">{t.mention_rate}%</td>
                        <td className="py-2 px-4 text-right text-[#8A6D00] font-medium">{t.sov}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent></Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Content brief modal */}
      <Dialog open={briefOpen} onOpenChange={setBriefOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="aiv-brief-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wand2 className="w-4 h-4 text-[#1D4ED8]" /> Content brief</DialogTitle>
          </DialogHeader>
          {brief && (
            <div className="space-y-4">
              <div className="text-[11px] text-[#8A857A]">{brief._cat} · answers: “{brief._q}”</div>
              <div>
                <div className="text-lg font-semibold text-[#1A1A1A]">{brief.title}</div>
                {brief.angle && <p className="text-sm text-[#59554D] mt-1">{brief.angle}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] p-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#8A857A] mb-1">Target question</div>
                  <div className="text-[#59554D]">{brief.target_question}</div>
                </div>
                <div className="rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] p-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#8A857A] mb-1">Format</div>
                  <div className="text-[#59554D]">{brief.suggested_format}</div>
                </div>
              </div>
              {Array.isArray(brief.key_points) && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#8A857A] mb-1.5">Key points to cover</div>
                  <ul className="space-y-1.5">
                    {brief.key_points.map((k, i) => (
                      <li key={i} className="flex gap-2 text-sm text-[#59554D]"><span className="text-[#1D4ED8]">•</span>{k}</li>
                    ))}
                  </ul>
                </div>
              )}
              {Array.isArray(brief.keywords) && (
                <div className="flex flex-wrap gap-1.5">
                  {brief.keywords.map((k, i) => <Badge key={i} className="bg-[#DAD5CA] text-[#59554D] border-0 text-[11px]">{k}</Badge>)}
                </div>
              )}
              {brief.meta_description && (
                <div className="rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] p-3 text-sm">
                  <div className="text-[10px] uppercase tracking-wider text-[#8A857A] mb-1">Meta description</div>
                  <div className="text-[#59554D]">{brief.meta_description}</div>
                </div>
              )}
              {brief.cta && <div className="text-sm text-[#0E6B3F]">CTA: {brief.cta}</div>}
              <div className="flex justify-end">
                <Button onClick={copyBrief} className="bg-[#1D4ED8] hover:bg-[#EFF6FF] text-[#1A1A1A]" data-testid="aiv-brief-copy">
                  <Copy className="w-4 h-4 mr-2" /> Copy brief
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
